import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import {
    ArrowRight, ShoppingBag, Cpu, Code, Truck, Briefcase, Download,
    Star, ChevronLeft, ChevronRight, CheckCircle2,
} from "lucide-react";

const QUICK_CARDS = [
    { icon: ShoppingBag, title: "Shop Products", desc: "Order detergents online", to: "/products?category=detergents" },
    { icon: Cpu, title: "ICT Consultation", desc: "Network, cyber & support", to: "/services#ict" },
    { icon: Code, title: "Mobile App Dev", desc: "Bring your app idea to life", to: "/services#software" },
    { icon: Truck, title: "Track Orders", desc: "Check delivery status", to: "/profile" },
    { icon: Briefcase, title: "Careers", desc: "Join our team", to: "/careers" },
    { icon: Download, title: "Downloads", desc: "Brochures & catalogues", to: "/downloads" },
];

const STATS = [
    { value: 12500, label: "Customers served" },
    { value: 480000, label: "Products sold" },
    { value: 65, label: "Projects completed" },
    { value: 28, label: "Mobile apps shipped" },
];

const BUSINESS_AREAS = [
    {
        title: "Home Detergents",
        tag: "FMCG",
        desc: "Liquid soaps, hand wash, laundry & cleaning products crafted in Uganda.",
        image: "https://images.unsplash.com/photo-1597931752949-98c74b5b159f?auto=format&fit=crop&w=1200&q=80",
        to: "/products?category=detergents",
        accent: "bg-primary",
    },
    {
        title: "ICT Services",
        tag: "Enterprise",
        desc: "Network installation, cyber security, IT support, training & consultancy.",
        image: "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1200&q=80",
        to: "/services#ict",
        accent: "bg-secondary",
    },
    {
        title: "Software Development",
        tag: "Digital",
        desc: "Mobile apps, web platforms, automation systems & enterprise solutions.",
        image: "https://images.unsplash.com/photo-1542831371-29b0f74f9713?auto=format&fit=crop&w=1200&q=80",
        to: "/services#software",
        accent: "bg-accent",
    },
];

function HeroSlider({ banners }) {
    const [idx, setIdx] = useState(0);
    useEffect(() => {
        if (banners.length < 2) return;
        const t = setInterval(() => setIdx((i) => (i + 1) % banners.length), 6000);
        return () => clearInterval(t);
    }, [banners.length]);

    if (!banners.length) return null;
    const b = banners[idx];

    return (
        <section className="relative h-[78vh] min-h-[520px] w-full overflow-hidden bg-ink" data-testid="hero-slider">
            {banners.map((banner, i) => (
                <div
                    key={i}
                    className={`absolute inset-0 transition-opacity duration-1000 ${i === idx ? "opacity-100" : "opacity-0"}`}
                >
                    <img src={banner.image_url} alt={banner.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 hero-overlay" />
                </div>
            ))}

            <div className="relative h-full max-w-[1400px] mx-auto px-4 md:px-8 flex flex-col justify-center text-white">
                <div className="max-w-2xl animate-fade-up" key={idx}>
                    <div className="overline text-white/80 mb-4">Peppa Target Services</div>
                    <h1 className="font-display text-5xl md:text-7xl font-bold leading-[0.95] tracking-tight mb-6">
                        {b.title}
                    </h1>
                    <p className="text-lg md:text-xl text-white/80 max-w-xl mb-8 leading-relaxed">
                        {b.subtitle}
                    </p>
                    <div className="flex flex-wrap gap-3">
                        <Link
                            to={b.cta_link || "/products"}
                            className="group inline-flex items-center gap-2 bg-primary text-white px-7 py-4 font-semibold hover:bg-primary-hover transition-colors"
                            data-testid="hero-cta-primary"
                        >
                            {b.cta_text || "Learn more"}
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </Link>
                        <Link
                            to="/services"
                            className="inline-flex items-center gap-2 border border-white/40 text-white px-7 py-4 font-semibold hover:bg-white hover:text-ink transition-colors"
                            data-testid="hero-cta-secondary"
                        >
                            View services
                        </Link>
                    </div>
                </div>
            </div>

            {banners.length > 1 && (
                <>
                    <div className="absolute bottom-8 right-4 md:right-8 flex items-center gap-3 z-10">
                        <button
                            onClick={() => setIdx((idx - 1 + banners.length) % banners.length)}
                            className="w-10 h-10 border border-white/40 text-white hover:bg-white hover:text-ink transition-colors flex items-center justify-center"
                            data-testid="hero-prev-btn"
                            aria-label="Previous"
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => setIdx((idx + 1) % banners.length)}
                            className="w-10 h-10 border border-white/40 text-white hover:bg-white hover:text-ink transition-colors flex items-center justify-center"
                            data-testid="hero-next-btn"
                            aria-label="Next"
                        >
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                    <div className="absolute bottom-8 left-4 md:left-8 flex gap-2">
                        {banners.map((_, i) => (
                            <button
                                key={i}
                                onClick={() => setIdx(i)}
                                className={`h-1 transition-all ${i === idx ? "w-10 bg-primary" : "w-5 bg-white/40"}`}
                                aria-label={`Slide ${i + 1}`}
                            />
                        ))}
                    </div>
                </>
            )}
        </section>
    );
}

function Counter({ value, label }) {
    const [v, setV] = useState(0);
    useEffect(() => {
        const duration = 1500;
        const start = performance.now();
        let raf;
        const step = (t) => {
            const p = Math.min(1, (t - start) / duration);
            setV(Math.floor(value * p));
            if (p < 1) raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, [value]);
    return (
        <div className="text-center">
            <div className="font-display text-4xl md:text-6xl font-bold text-white tabular-nums">
                {v.toLocaleString()}+
            </div>
            <div className="mt-2 text-sm text-white/60 uppercase tracking-[0.2em]">{label}</div>
        </div>
    );
}

export default function Home() {
    const [banners, setBanners] = useState([]);
    const [featured, setFeatured] = useState([]);
    const [news, setNews] = useState([]);
    const [testimonials, setTestimonials] = useState([]);
    const { format } = useCurrency();

    useEffect(() => {
        api.get("/banners").then((r) => setBanners(r.data)).catch(() => {});
        api.get("/products?featured=true&limit=8").then((r) => setFeatured(r.data)).catch(() => {});
        api.get("/news?limit=3").then((r) => setNews(r.data)).catch(() => {});
        api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    }, []);

    return (
        <div data-testid="home-page">
            <HeroSlider banners={banners.length ? banners : [{
                title: "Quality you can feel.",
                subtitle: "Premium home detergents, ICT services & software – proudly made in Uganda.",
                image_url: "https://images.pexels.com/photos/934350/pexels-photo-934350.jpeg",
                cta_text: "Shop now",
                cta_link: "/products",
            }]} />

            {/* Quick Service Cards */}
            <section className="relative -mt-16 z-10 max-w-[1400px] mx-auto px-4 md:px-8" data-testid="quick-cards">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
                    {QUICK_CARDS.map((c) => (
                        <Link
                            key={c.title}
                            to={c.to}
                            className="group bg-white border border-border p-5 hover:border-primary hover:-translate-y-1 transition-all duration-300 shadow-sm hover:shadow-lg"
                            data-testid={`quick-card-${c.title.toLowerCase().replace(/\s/g, '-')}`}
                        >
                            <c.icon className="w-7 h-7 text-primary mb-3" />
                            <div className="font-display font-semibold text-sm md:text-base">{c.title}</div>
                            <div className="text-xs text-muted-foreground mt-1">{c.desc}</div>
                        </Link>
                    ))}
                </div>
            </section>

            {/* About Teaser */}
            <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-24 md:py-32 grid md:grid-cols-2 gap-12 items-center" data-testid="about-teaser">
                <div>
                    <div className="overline text-primary mb-4">Who we are</div>
                    <h2 className="font-display text-4xl md:text-6xl font-bold leading-[1.05] tracking-tight mb-6">
                        One company.<br/>
                        <span className="text-primary">Three excellences.</span>
                    </h2>
                    <p className="text-lg text-muted-foreground leading-relaxed mb-6 max-w-xl">
                        From shelves to server rooms to source code – Peppa Target Services Limited is a multi-domain Ugandan
                        enterprise built on craftsmanship, integrity, and a relentless drive to deliver outcomes our customers
                        can measure.
                    </p>
                    <ul className="space-y-3 mb-8">
                        {["ISO-aligned manufacturing", "Certified ICT engineers", "Agile software delivery"].map((p) => (
                            <li key={p} className="flex items-center gap-2 text-sm">
                                <CheckCircle2 className="w-4 h-4 text-secondary" />
                                <span>{p}</span>
                            </li>
                        ))}
                    </ul>
                    <Link to="/about" className="inline-flex items-center gap-2 font-semibold text-primary hover:gap-3 transition-all" data-testid="about-teaser-link">
                        Read our story <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
                <div className="aspect-[4/5] overflow-hidden">
                    <img
                        src="https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1200&q=80"
                        alt="Peppa Target office"
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                    />
                </div>
            </section>

            {/* Business Areas Bento */}
            <section className="bg-surface py-24 md:py-32" data-testid="business-areas">
                <div className="max-w-[1400px] mx-auto px-4 md:px-8">
                    <div className="mb-12 max-w-2xl">
                        <div className="overline text-primary mb-4">Business areas</div>
                        <h2 className="font-display text-4xl md:text-5xl font-bold leading-tight">
                            Built for homes, offices and digital frontiers.
                        </h2>
                    </div>
                    <div className="grid md:grid-cols-3 gap-6">
                        {BUSINESS_AREAS.map((b) => (
                            <Link
                                key={b.title}
                                to={b.to}
                                className="group bg-white border border-border overflow-hidden hover:-translate-y-1 transition-transform duration-300"
                                data-testid={`business-area-${b.title.toLowerCase().replace(/\s/g, '-')}`}
                            >
                                <div className="relative aspect-[4/3] overflow-hidden">
                                    <img src={b.image} alt={b.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                                    <span className={`absolute top-4 left-4 ${b.accent} text-white text-[10px] font-bold px-2 py-1 uppercase tracking-wider`}>
                                        {b.tag}
                                    </span>
                                </div>
                                <div className="p-6">
                                    <h3 className="font-display text-2xl font-bold mb-2">{b.title}</h3>
                                    <p className="text-sm text-muted-foreground leading-relaxed mb-4">{b.desc}</p>
                                    <span className="inline-flex items-center gap-2 text-sm font-semibold text-primary group-hover:gap-3 transition-all">
                                        Explore <ArrowRight className="w-4 h-4" />
                                    </span>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Featured Products */}
            <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-24 md:py-32" data-testid="featured-products">
                <div className="flex items-end justify-between mb-10">
                    <div>
                        <div className="overline text-primary mb-4">Featured</div>
                        <h2 className="font-display text-4xl md:text-5xl font-bold">Best-sellers & new arrivals</h2>
                    </div>
                    <Link to="/products" className="hidden md:inline-flex items-center gap-2 font-semibold text-primary hover:gap-3 transition-all">
                        View all <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                    {featured.slice(0, 8).map((p) => (
                        <Link
                            key={p.id}
                            to={`/products/${p.slug}`}
                            className="group bg-white border border-border hover:border-primary transition-all"
                            data-testid={`featured-product-${p.slug}`}
                        >
                            <div className="aspect-square overflow-hidden bg-surface">
                                <img src={p.image_url} alt={p.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                            </div>
                            <div className="p-4">
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{p.category}</div>
                                <h3 className="font-display font-semibold text-sm leading-tight line-clamp-2 mb-2">{p.name}</h3>
                                <div className="flex items-center justify-between">
                                    <span className="font-display font-bold text-base">{format(p.price)}</span>
                                    <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                                        <Star className="w-3 h-3 fill-accent text-accent" /> {p.rating}
                                    </span>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>

            {/* Stats */}
            <section className="bg-ink py-20 md:py-24" data-testid="stats-section">
                <div className="max-w-[1400px] mx-auto px-4 md:px-8 grid grid-cols-2 md:grid-cols-4 gap-8">
                    {STATS.map((s) => <Counter key={s.label} {...s} />)}
                </div>
            </section>

            {/* News */}
            {news.length > 0 && (
                <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-24 md:py-32" data-testid="latest-news">
                    <div className="flex items-end justify-between mb-10">
                        <div>
                            <div className="overline text-primary mb-4">Latest news</div>
                            <h2 className="font-display text-4xl md:text-5xl font-bold">From our newsroom</h2>
                        </div>
                        <Link to="/news" className="hidden md:inline-flex items-center gap-2 font-semibold text-primary">
                            All news <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                    <div className="grid md:grid-cols-3 gap-6">
                        {news.map((n) => (
                            <Link
                                key={n.id}
                                to={`/news/${n.slug}`}
                                className="group bg-white border border-border overflow-hidden hover:border-primary transition-all"
                                data-testid={`news-card-${n.slug}`}
                            >
                                <div className="aspect-[16/10] overflow-hidden">
                                    <img src={n.image_url} alt={n.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                                </div>
                                <div className="p-6">
                                    <div className="text-[10px] uppercase tracking-wider text-primary font-semibold mb-2">{n.category}</div>
                                    <h3 className="font-display font-bold text-lg leading-snug mb-3">{n.title}</h3>
                                    <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2">{n.excerpt}</p>
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>
            )}

            {/* Testimonials */}
            {testimonials.length > 0 && (
                <section className="bg-surface py-24 md:py-32" data-testid="testimonials">
                    <div className="max-w-[1400px] mx-auto px-4 md:px-8">
                        <div className="mb-12 max-w-2xl">
                            <div className="overline text-primary mb-4">Trusted by</div>
                            <h2 className="font-display text-4xl md:text-5xl font-bold">Words from our customers.</h2>
                        </div>
                        <div className="grid md:grid-cols-3 gap-6">
                            {testimonials.slice(0, 3).map((t) => (
                                <div key={t.id} className="bg-white border border-border p-8" data-testid={`testimonial-${t.name.toLowerCase().replace(/\s/g, '-')}`}>
                                    <div className="flex gap-0.5 mb-4">
                                        {Array.from({ length: t.rating }).map((_, i) => (
                                            <Star key={i} className="w-4 h-4 fill-accent text-accent" />
                                        ))}
                                    </div>
                                    <p className="font-display text-lg leading-snug mb-6 text-ink">"{t.quote}"</p>
                                    <div className="flex items-center gap-3">
                                        {t.avatar_url && (
                                            <img src={t.avatar_url} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                                        )}
                                        <div>
                                            <div className="font-semibold text-sm">{t.name}</div>
                                            <div className="text-xs text-muted-foreground">{t.role}{t.company ? `, ${t.company}` : ''}</div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Partners marquee */}
            <section className="py-16 border-t border-border overflow-hidden" data-testid="partners">
                <div className="text-center mb-8">
                    <div className="overline text-muted-foreground">Trusted partners & clients</div>
                </div>
                <div className="flex animate-marquee whitespace-nowrap gap-16">
                    {[...Array(2)].map((_, i) => (
                        <div key={i} className="flex gap-16 items-center">
                            {["AURORA", "NEXUS GROUP", "AKILI TECH", "KARIBU FOODS", "ZEBRA LOGISTICS", "MAVUNO COOP", "SAFARI BANK", "JAMBO HEALTH"].map((p, j) => (
                                <div key={j} className="font-display font-black text-2xl text-muted-foreground/40 hover:text-primary transition-colors">
                                    {p}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
}
